// ============================================================
// useWoundDisplay - may this viewer read the wound numbers?
//
// One question, asked by five readouts and the log. A hook rather than a
// prop threaded down from the panels because `CombatantVitals` alone is
// mounted by four different parents; plumbing a boolean through all of
// them would put the same `isGm && !banded` expression in four places,
// which is how the four disagreeing health ramps happened.
//
// NOT A SECURITY BOUNDARY. The wounds are in the client's memory either
// way; this decides what is on screen. Genuinely hiding a number would
// mean a GM-only document, which is a different feature.
// ============================================================
import { useContext } from 'react';
import SessionContext from '../contexts/SessionContext';
import { resolveBandedWounds } from '../engine/settings/resolve';
import type { PermissionsDoc } from '../engine/settings/permissions';

/** Only the three fields this hook needs off the session. */
interface SessionSlice {
  isGm?: boolean;
  sessionId?: string | null;
  permissions?: PermissionsDoc | null;
}

export interface WoundDisplay {
  /** Print `7 / 22`, or a band name. */
  showNumbers: boolean;
}

/**
 * The GM always sees numbers - they are running the fight, and the whole
 * point of the ruling is that they know what the players do not. Solo
 * play has no session and therefore nobody to hide from.
 */
export function useWoundDisplay(): WoundDisplay {
  // THE CONTEXT DIRECTLY, not `useSession`, which throws when there is no
  // provider. These are leaf readouts: the vitals strip alone is mounted
  // by four parents, and a preview, a gallery card or a unit test may
  // render one with no session around it at all. No session means nobody
  // to hide from, which is the same answer as solo play.
  // Asserted at the boundary because SessionContext is a `.jsx` module
  // created with `createContext(null)`, so TypeScript infers `never`.
  const ctx = useContext(SessionContext) as SessionSlice | null;
  if (!ctx?.sessionId || ctx.isGm) return { showNumbers: true };
  return { showNumbers: !resolveBandedWounds(ctx.permissions) };
}
